import clsx from 'clsx';
import { Paperclip, Plus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useAnswerChange, useChanges, useClientRole, useDecideChange, useRequestChange, useUpload } from '@/api';
import type { ChangeRequest, ChangeUrgency } from '@/api/types';
import { ClientChangeDialog } from '@/components/ChangeDialog';
import { ViaApi } from '@/components/ViaApi';
import { QueryPending } from '@/components/QueryState';
import { Dialog, Empty, Status } from '@/components/ui';
import { clientCan, OWNER_DECIDES } from '@/lib/access';
import { formatMoney, formatWhen } from '@/lib/format';
import { initiativeStatus, takeNote, urgencyLabel, viaApi } from '@/lib/initiatives';
import type { ProjectContext } from './ProjectShell';

type Picked = { key: number; name: string; status: 'uploading' | 'done' | 'error'; id?: string; error?: string };

/** The client answers the studio's question right on the card; the initiative goes back to the studio. */
function Answer({ c }: { c: ChangeRequest }) {
  const answer = useAnswerChange();
  const [text, setText] = useState('');
  return (
    <div className="mt-3 rounded bg-panel p-3 shadow-hair">
      <p className="text-sm">
        <span className="font-medium">Вопрос студии: </span>
        {c.question}
      </p>
      <form
        className="mt-2 flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) answer.mutate({ id: c.id, text: text.trim() }, { onSuccess: () => setText('') });
        }}
      >
        <textarea className="field min-h-10 flex-1" aria-label="Ваш ответ" placeholder="Ваш ответ" value={text} onChange={(e) => setText(e.target.value)} />
        <button className="btn-primary self-start" disabled={!text.trim() || answer.isPending}>
          Ответить
        </button>
      </form>
      {answer.error && (
        <p className="mt-2 text-sm text-overdue" role="alert">
          {(answer.error as Error).message}
        </p>
      )}
    </div>
  );
}

/** What the studio answered, in one line under the card. */
function StudioAnswer({ c }: { c: ChangeRequest }) {
  if (c.status === 'declined' && c.declinedBy === 'studio') return <p className="mt-2 text-sm">{c.declineReason ? `Не берём: ${c.declineReason}` : 'Студия не взяла запрос в работу.'}</p>;
  if (c.status === 'parked') return <p className="mt-2 text-sm">Отложили{c.parkNote ? `: ${c.parkNote}` : ''}. Когда вернёмся к этому, напишем здесь.</p>;
  if (c.status === 'in_scope') {
    const note = takeNote(c);
    return <p className="mt-2 text-sm">Взяли в работу без доплаты{note ? `: ${note}` : '.'}</p>;
  }
  return null;
}

const URGENCY: ChangeUrgency[] = ['low', 'month', 'urgent'];

/** «Идеи и запросы»: the client sends an initiative; the studio takes it, estimates it, asks, parks or declines it. */
export function ProjectChanges() {
  const { project } = useOutletContext<ProjectContext>();
  const changes = useChanges(project.id);
  const data = changes.data;
  const request = useRequestChange();
  const decide = useDecideChange();
  const upload = useUpload();
  const role = useClientRole();
  const owner = clientCan(role, 'admin');
  const [open, setOpen] = useState(false);
  const [deciding, setDeciding] = useState<{ change: ChangeRequest; accept: boolean }>();
  const [shown, setShown] = useState<{ id: string; title: string }>();
  const [title, setTitle] = useState('');
  const [goal, setGoal] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState<ChangeUrgency>('low');
  const [picked, setPicked] = useState<Picked[]>([]);
  const seq = useRef(0);
  const uploading = picked.some((p) => p.status === 'uploading');
  const ready = !!title.trim() && !!goal.trim() && !uploading;

  async function pick(files: File[]) {
    for (const f of files) {
      const key = ++seq.current;
      setPicked((all) => [...all, { key, name: f.name, status: 'uploading' }]);
      const set = (patch: Partial<Picked>) => setPicked((all) => all.map((x) => (x.key === key ? { ...x, ...patch } : x)));
      try {
        const d = await upload.mutateAsync({ projectId: project.id, name: f.name, sizeKb: Math.max(1, Math.round(f.size / 1024)), file: f });
        set({ status: 'done', id: d.id });
      } catch (e) {
        set({ status: 'error', error: (e as Error).message });
      }
    }
  }

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
      <section className="min-w-0">
        {!data ? (
          <QueryPending query={changes} />
        ) : data.length === 0 ? (
          <Empty title="Идей и запросов пока нет">
            Присылайте всё, что приходит в голову: идеи, пожелания, новые задачи. Каждая сразу попадает к студии, и студия ответит на каждую: возьмёт в работу, оценит отдельно, задаст вопрос или объяснит, почему не берёт.
          </Empty>
        ) : (
          <ul className="divide-y divide-line border-y border-line">
            {data.map((c) => (
              <li key={c.id} className="py-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <button type="button" className="text-left font-medium underline-offset-2 hover:underline" onClick={() => setShown({ id: c.id, title: c.title })}>
                    {c.title}
                  </button>
                  <Status tone={initiativeStatus[c.status].tone}>{initiativeStatus[c.status].client}</Status>
                </div>
                {c.goal && <p className="mt-1 text-sm">{c.goal}</p>}
                {c.description && <p className="mt-1 text-sm text-muted">{c.description}</p>}
                <p className="mt-1 text-xs text-faint">
                  {c.author.side === 'client' && !viaApi(c) ? 'Вы' : c.author.name}, {formatWhen(c.createdAt)}
                  {c.urgency && ` · ${urgencyLabel[c.urgency]}`}
                  {!!c.attachments?.length && ` · файлов: ${c.attachments.length}`}
                </p>
                <ViaApi c={c} />
                <StudioAnswer c={c} />
                {c.status === 'question' && <Answer c={c} />}
                {c.task && (
                  <Link to={`/p/${project.id}/tasks/${c.task.id}`} className="mt-2 inline-block text-sm underline underline-offset-2">
                    Задача {c.task.key}
                  </Link>
                )}
                {c.estimate && (
                  <div className="mt-3 rounded bg-panel p-3 shadow-hair">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="num text-md font-semibold">{formatMoney(c.estimate.price)}</span>
                      <span className="text-sm text-muted">{c.estimate.days} рабочих дней</span>
                    </div>
                    <p className="mt-1 text-sm">{c.estimate.note}</p>
                    {c.status === 'estimated' && !owner && role && <p className="mt-3 text-sm text-muted">{OWNER_DECIDES}</p>}
                    {c.status === 'estimated' && owner && (
                      <div className="mt-3 flex gap-2">
                        <button className="btn-primary" disabled={decide.isPending} onClick={() => setDeciding({ change: c, accept: true })}>
                          Принять — выставить счёт
                        </button>
                        <button className="btn-ghost" disabled={decide.isPending} onClick={() => setDeciding({ change: c, accept: false })}>
                          Не нужно
                        </button>
                      </div>
                    )}
                    {c.invoiceId && (
                      <Link to={`/money/${c.invoiceId}`} className="mt-2 inline-block text-sm underline underline-offset-2">
                        Счёт по запросу
                      </Link>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {decide.error && <p className="mt-3 text-sm text-overdue" role="alert">{(decide.error as Error).message}</p>}
      </section>

      <aside className="lg:sticky lg:top-[80px] lg:self-start">
        <div className="rounded-lg p-5 shadow-hair">
          <h2 className="font-semibold">Предложить идею или задачу</h2>
          <p className="mt-1 text-sm text-muted">Готовое ТЗ не нужно: опишите, что хотите и зачем. Студия ответит на каждую: возьмёт в работу, оценит отдельно, уточнит или объяснит, почему не берёт.</p>
          {open ? (
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!ready) return;
                const documentIds = picked.filter((p) => p.status === 'done' && p.id).map((p) => p.id!);
                request.mutate(
                  { projectId: project.id, title: title.trim(), goal: goal.trim(), description: description.trim(), urgency, documentIds },
                  {
                    onSuccess: () => {
                      setTitle('');
                      setGoal('');
                      setDescription('');
                      setUrgency('low');
                      setPicked([]);
                      setOpen(false);
                    },
                  },
                );
              }}
            >
              <label className="block text-sm">
                Что хотите
                <input className="field mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например: акция к Новому году на сайте" autoFocus />
              </label>
              <label className="block text-sm">
                Зачем, какой результат ожидаете
                <textarea className="field mt-1 min-h-20" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Например: больше заявок в декабре" />
              </label>
              <label className="block text-sm">
                Подробности, ссылки <span className="text-faint">— если есть</span>
                <textarea className="field mt-1 min-h-16" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Примеры, ссылки, к какому сроку" />
              </label>
              <fieldset>
                <legend className="text-sm">Насколько срочно</legend>
                <div className="mt-1 grid grid-cols-3 gap-1 rounded bg-panel p-1 shadow-hair">
                  {URGENCY.map((u) => (
                    <label key={u} className={clsx('cursor-pointer rounded px-2 py-1.5 text-center text-xs', urgency === u ? 'bg-ink text-inverse' : 'text-muted hover:text-ink')}>
                      <input type="radio" name="urgency" value={u} className="sr-only" checked={urgency === u} onChange={() => setUrgency(u)} />
                      {urgencyLabel[u]}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div>
                <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-muted hover:text-ink">
                  <Paperclip className="size-4" /> Приложить файлы
                  <input
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      e.target.value = '';
                      void pick(files);
                    }}
                  />
                </label>
                {!!picked.length && (
                  <ul className="mt-2 space-y-1 text-xs" aria-label="Файлы к запросу">
                    {picked.map((p) => (
                      <li key={p.key} className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate">{p.name}</span>
                        <span className={p.status === 'error' ? 'text-overdue' : 'text-faint'}>{p.status === 'uploading' ? 'загружается…' : p.status === 'error' ? p.error : 'готово'}</span>
                        <button type="button" aria-label={`Убрать ${p.name}`} className="text-faint hover:text-ink" onClick={() => setPicked((all) => all.filter((x) => x.key !== p.key))}>
                          <X className="size-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="btn-primary w-full" disabled={!ready || request.isPending}>
                Отправить студии
              </button>
              {request.error && <p className="text-sm text-overdue" role="alert">{(request.error as Error).message}</p>}
            </form>
          ) : (
            <button className="btn-secondary mt-4 w-full" onClick={() => setOpen(true)}>
              <Plus className="size-4" /> Новая идея или запрос
            </button>
          )}
        </div>
      </aside>
      <Dialog open={!!deciding} onClose={() => setDeciding(undefined)} title={deciding?.accept ? 'Принять оценку?' : 'Закрыть запрос?'}>
        {deciding && (
          <div className="space-y-4 p-5">
            <p className="text-sm">
              <span className="font-medium">{deciding.change.title}</span>
              {deciding.accept && deciding.change.estimate ? (
                <span className="mt-1 block text-muted">
                  Будет выставлен счёт на {formatMoney(deciding.change.estimate.price)}, срок работы {deciding.change.estimate.days} рабочих дней. Счёт придёт в раздел «Деньги», оплатить его нужно в течение 5 дней.
                </span>
              ) : (
                <span className="mt-1 block text-muted">Запрос будет закрыт без доплаты, счёт не выставляется. Передумать позже можно, создав новый запрос.</span>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setDeciding(undefined)}>
                Отмена
              </button>
              <button
                className={deciding.accept ? 'btn-primary' : 'btn-secondary'}
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: deciding.change.id, accept: deciding.accept }, { onSettled: () => setDeciding(undefined) })}
              >
                {deciding.accept ? 'Принять и выставить счёт' : 'Закрыть запрос'}
              </button>
            </div>
          </div>
        )}
      </Dialog>
      {shown && <ClientChangeDialog id={shown.id} title={shown.title} onClose={() => setShown(undefined)} />}
    </div>
  );
}
